/**
 * Investitionsbedingter Gewinnfreibetrag / IFB (AT) — SB-Steuerung.
 *
 * Juni 2026 — Bringt den im FL-Tax-Modul berechneten Investitionsfreibetrag (§10 EStG)
 * in den SB-Workflow: Aufschlüsselung (Gewinn, SVS, Grund-/investitionsbedingter
 * Freibetrag), Steuerersparnis-Schätzung und – mit Vollmacht – auditierte Festlegung
 * des Betrags inkl. optionaler Mandanten-Benachrichtigung.
 *
 * Selbst-gatend: rendert nichts, wenn der Mandant nicht AT (oder Kleinunternehmer) ist.
 *
 * Endpoints:
 *   GET  /api/advisor/clients/{cid}/investment-allowance?year=
 *   PUT  /api/advisor/clients/{cid}/investment-allowance
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TrendingDown, PiggyBank, Loader2, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const H = () => ({ Authorization: `Bearer ${localStorage.getItem('advisor_token')}` });

const fmtEur = (n) => new Intl.NumberFormat('de-DE',
  { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(Number(n) || 0);

export default function AdvisorInvestmentAllowanceControl({ clientId, year, embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = year ? `?year=${year}` : '';
      const res = await axios.get(`${API}/advisor/clients/${clientId}/investment-allowance${q}`, { headers: H() });
      setData(res.data);
      setAmount(res.data?.investitionsbedingt != null ? String(res.data.investitionsbedingt) : '');
    } catch {
      /* still usable without — render nothing */
    } finally {
      setLoading(false);
    }
  }, [clientId, year]);

  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    if (!reason.trim()) { toast.error('Bitte eine Begründung angeben (GoBD-Pflicht).'); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/advisor/clients/${clientId}/investment-allowance`, {
        amount: parseFloat(amount || '0'),
        reason: reason.trim(),
        year: data?.year,
        notify_client: notify,
      }, { headers: H() });
      setData((d) => ({ ...d, ...res.data }));
      setAmount(res.data?.investitionsbedingt != null ? String(res.data.investitionsbedingt) : '');
      setReason('');
      toast.success(res.data?.notified
        ? 'Investitionsfreibetrag gesetzt · Mandant informiert'
        : 'Investitionsfreibetrag gesetzt');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data || !data.available) return null;

  const canEdit = !!data.can_edit;
  const max = Number(data.max_amount) || 0;
  const overMax = parseFloat(amount || '0') > max;

  return (
    <div className={`bg-white border border-zinc-200 rounded-xl p-4 ${embedded ? '' : 'shadow-sm'}`} data-testid="advisor-ifb-control">
      <div className="flex items-center gap-2 mb-1">
        <PiggyBank className="h-4 w-4 text-emerald-600" />
        <h4 className="font-semibold text-slate-900 text-sm">Investitionsfreibetrag · Gewinnfreibetrag (AT)</h4>
        <span className="text-[11px] text-slate-400">{data.year}</span>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Investitionsbedingter Gewinnfreibetrag §10 EStG — berechnet aus dem Jahresgewinn nach SVS.
      </p>

      {/* Aufschlüsselung */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
        <Stat label="Gewinn (netto)" value={fmtEur(data.profit)} />
        <Stat label="SVS-Beiträge" value={`−${fmtEur(data.svs)}`} />
        <Stat label="Gewinn nach SVS" value={fmtEur(data.profit_after_svs)} />
        <Stat label="Grundfreibetrag" value={fmtEur(data.grundfreibetrag)} />
        <Stat label="Max. invest.-bedingt" value={fmtEur(data.max_amount)} accent="text-emerald-700" />
        <Stat label="Aktuell gesetzt" value={fmtEur(data.investitionsbedingt)} accent="text-slate-900" />
      </div>

      {/* Steuerersparnis */}
      <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 mb-3" data-testid="ifb-tax-saving">
        <TrendingDown className="h-4 w-4 text-emerald-600 shrink-0" />
        <div className="text-xs text-emerald-800">
          Geschätzte ESt-Ersparnis aktuell: <strong>{fmtEur(data.tax_saving)}</strong>
          <span className="text-emerald-600"> · bei Ausschöpfung des Maximums: {fmtEur(data.max_tax_saving)}</span>
        </div>
      </div>

      {max <= 0 ? (
        <p className="text-xs text-slate-400 flex items-center gap-1.5" data-testid="ifb-not-applicable">
          <Info className="h-3.5 w-3.5" /> Kein investitionsbedingter Freibetrag möglich (Gewinn ≤ 33.000 €).
        </p>
      ) : !canEdit ? (
        <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="ifb-readonly">
          <Info className="h-3.5 w-3.5" /> Read-only — der Mandant hat dir keine Vollmacht erteilt.
        </p>
      ) : (
        <div className="space-y-2.5 border-t border-zinc-100 pt-3">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs">Investitionsbedingter Freibetrag (€)</Label>
              <Input
                type="number" min={0} max={max} step={100}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={overMax ? 'border-rose-300' : ''}
                data-testid="ifb-amount-input"
              />
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setAmount(String(max))} data-testid="ifb-max-btn">
              Max ({fmtEur(max)})
            </Button>
          </div>
          {overMax && <p className="text-[11px] text-rose-600">Wird beim Speichern auf das Maximum gedeckelt.</p>}

          <div>
            <Label className="text-xs">Begründung (Pflicht · GoBD)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="z.B. Investition in Maschine geplant (Behaltefrist 4 J.) — abgestimmt mit Mandant am …"
              data-testid="ifb-reason-input"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="h-4 w-4" data-testid="ifb-notify-toggle" />
            <span className="text-xs text-slate-600">Mandant per Notiz informieren</span>
          </label>

          <Button onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ifb-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            Freibetrag festlegen
          </Button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = 'text-slate-700' }) {
  return (
    <div className="rounded-lg bg-slate-50 px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-sm font-semibold ${accent}`}>{value}</div>
    </div>
  );
}
